import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiEye, FiEyeOff, FiCopy, FiShield, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import Header from './Header';
import AppLoading from './AppLoading';
import '../styles/RecoveryPhrasePage.css';

const RecoveryPhrasePage = () => {
  const [showPhrase, setShowPhrase] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recoveryPhrase, setRecoveryPhrase] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { wallet, loading: walletLoading } = useWallet();

  useEffect(() => {
    const loadRecoveryPhrase = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Wait for wallet context to be ready
        if (walletLoading) {
          return;
        }

        // Check if wallet exists in context
        if (!wallet) {
          throw new Error('No wallet found. Please create or import a wallet first.');
        }

        // Get wallet data from localStorage as fallback
        const walletData = localStorage.getItem('wallet');
        if (!walletData) {
          throw new Error('No wallet data found. Please create or import a wallet first.');
        }

        let parsedData;
        try {
          parsedData = JSON.parse(walletData);
        } catch (parseError) {
          console.error('Error parsing wallet data:', parseError);
          throw new Error('Invalid wallet data format. Please create a new wallet.');
        }

        // Check if mnemonic exists in the wallet data
        if (!parsedData.mnemonic) {
          throw new Error('No recovery phrase found in wallet data. Please create a new wallet.');
        }

        // Validate mnemonic format
        const mnemonic = parsedData.mnemonic;
        if (typeof mnemonic !== 'string' && !Array.isArray(mnemonic)) {
          throw new Error('Invalid recovery phrase format. Please create a new wallet.');
        }

        // Join the mnemonic array into a space-separated string if it's an array
        const mnemonicPhrase = Array.isArray(mnemonic)
          ? mnemonic.join(' ')
          : mnemonic;

        // Validate that we have a proper 12-word phrase
        const words = mnemonicPhrase.trim().split(/\s+/);
        if (words.length !== 12) {
          throw new Error('Invalid recovery phrase length. Expected 12 words.');
        }

        setRecoveryPhrase(mnemonicPhrase);
      } catch (error) {
        console.error('Error loading recovery phrase:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadRecoveryPhrase();

    // Cleanup function
    return () => {
      setRecoveryPhrase('');
      setShowPhrase(false);
    };
  }, [wallet, walletLoading]);

  const handleCopyPhrase = async () => {
    if (!recoveryPhrase) return;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(recoveryPhrase);
        setCopied(true);
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (err) {
      console.warn('Clipboard API failed, using fallback:', err);
      // Fallback for older browsers or non-secure contexts
      try {
        const textArea = document.createElement('textarea');
        textArea.value = recoveryPhrase;

        // Ensure textarea is not visible but part of DOM
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '0';
        document.body.appendChild(textArea);

        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          setCopied(true);
        } else {
          console.error('Fallback copy failed');
        }
      } catch (fallbackErr) {
        console.error('Fallback copy error:', fallbackErr);
      }
    }

    if (copied) {
      setTimeout(() => setCopied(false), 2000);
    } else {
      // Re-set timeout even if we just set copied=true above, logic needs to be inside.
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Early return for loading state
  if (isLoading || walletLoading) {
    return (
      <div className="recovery-page">
        <Header title="Recovery Phrase" className="sticky-header" />
        <div className="recovery-content">
          <div className="loading-message">
            Loading recovery phrase...
          </div>
        </div>
      </div>
    );
  }

  // Early return for error state
  if (error) {
    return (
      <div className="recovery-page">
        <Header title="Recovery Phrase" className="sticky-header" />
        <div className="recovery-content">
          <div className="error-message">
            <p>{error}</p>
            <button
              className="back-button"
              onClick={() => navigate('/settings')}
            >
              Back to Settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="recovery-page">
      <Header title="Recovery Phrase" className="sticky-header" />
      <div className="recovery-content">
        <motion.div
          className="recovery-card"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="recovery-header">
            <motion.div
              className="header-icon"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <FiShield />
            </motion.div>
            <h2>Your Recovery Phrase</h2>
            <p>These 12 words are the key to your TON and BNB wallets. Keep them safe and secure.</p>
          </div>

          <div className="warning-box">
            <span>🚨 Never share your recovery phrase with anyone! These 12 words control both your TON and BNB wallets.</span>
          </div>

          {recoveryPhrase ? (
            <>
              <div className="phrase-container">
                <div className={`phrase-content ${!showPhrase ? 'blurred' : ''}`}>
                  {recoveryPhrase.split(' ').slice(0, 12).map((word, index) => (
                    <div key={index} className="word-box">
                      <span className="word-number">{index + 1}</span>
                      <span className="word">{word}</span>
                    </div>
                  ))}
                </div>

                <button
                  className={`visibility-toggle ${showPhrase ? 'floating' : ''}`}
                  onClick={() => setShowPhrase(!showPhrase)}
                  aria-label={showPhrase ? 'Hide recovery phrase' : 'Show recovery phrase'}
                >
                  {showPhrase ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              <motion.button
                className="copy-button"
                onClick={handleCopyPhrase}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {copied ? <FiCheckCircle /> : <FiCopy />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </motion.button>
            </>
          ) : (
            <div className="no-phrase-message">
              <p>No recovery phrase found. Please create or import a wallet first.</p>
              <button
                className="back-button"
                onClick={() => navigate('/settings')}
              >
                Back to Settings
              </button>
            </div>
          )}

          <div className="security-tips">
            <h3>Important Security Tips</h3>
            <ul>
              <li>These 12 words are the master key to your funds</li>
              <li>Store your recovery phrase in a secure physical location</li>
              <li>Never screenshot or store it digitally</li>
              <li>Verify correct spelling of each word</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RecoveryPhrasePage; 